import React, { useState, useEffect } from "react";
import {
    Globe,
    Check,
    X,
    Copy,
    AlertCircle,
    Info,
    ExternalLink,
} from "lucide-react";
import { Bluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import PlanRestrictionAlert from "./PlanRestrictionAlert";

function DomainsPanel({
    workflowCustomDomain = null,
    workflowInternalDomain = null,
    isUpdatingWorkflow = false,
    onUpdateWorkflow,
    isCustomDomainFeatureEnabled = false,
}) {
    const { t } = useTranslation();

    const [editingCustomDomain, setEditingCustomDomain] = useState(false);
    const [editingInternalDomain, setEditingInternalDomain] = useState(false);
    const [tempCustomDomain, setTempCustomDomain] = useState(
        workflowCustomDomain || ""
    );
    const [tempInternalDomain, setTempInternalDomain] = useState(
        workflowInternalDomain?.replace(".agentui.app", "") || ""
    );
    const [customDomainError, setCustomDomainError] = useState(null);
    const [internalDomainError, setInternalDomainError] = useState(null);

    useEffect(() => {
        setTempCustomDomain(workflowCustomDomain || "");
    }, [workflowCustomDomain]);

    useEffect(() => {
        setTempInternalDomain(
            workflowInternalDomain?.replace(".agentui.app", "") || ""
        );
    }, [workflowInternalDomain]);

    const validateCustomDomain = (domain) => {
        if (!domain || domain.trim() === "") return true;
        const regex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
        return regex.test(domain.trim());
    };

    const validateInternalDomain = (domain) => {
        if (!domain || domain.trim() === "") return false;
        const regex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/i;
        return regex.test(domain.trim());
    };

    const handleSaveCustomDomain = async () => {
        setCustomDomainError(null);
        const cleanedDomain = tempCustomDomain.trim();

        if (cleanedDomain && !validateCustomDomain(cleanedDomain)) {
            setCustomDomainError(
                "Invalid domain format. Use format like app.example.com"
            );
            return;
        }

        try {
            await onUpdateWorkflow({ customDomain: cleanedDomain || null });
            setEditingCustomDomain(false);
            toast.success(
                t("appPreview.customDomainSaved") ||
                    "Custom domain saved successfully"
            );
        } catch (error) {
            setCustomDomainError(error.message || "Failed to update domain");
            toast.error(error.message || "Failed to update domain");
        }
    };

    const handleCancelCustomDomain = () => {
        setTempCustomDomain(workflowCustomDomain || "");
        setEditingCustomDomain(false);
        setCustomDomainError(null);
    };

    const handleSaveInternalDomain = async () => {
        setInternalDomainError(null);
        const cleanedDomain = tempInternalDomain.trim();

        if (!cleanedDomain || !validateInternalDomain(cleanedDomain)) {
            setInternalDomainError(
                "Invalid format. Use lowercase letters, numbers, and hyphens only"
            );
            return;
        }

        try {
            const appDomain = "agentui.app";
            const fullDomain = `${cleanedDomain}.${appDomain}`;
            await onUpdateWorkflow({ internalDomain: fullDomain });
            setEditingInternalDomain(false);
            toast.success(
                t("appPreview.internalDomainSaved") ||
                    "Internal domain saved successfully"
            );
        } catch (error) {
            console.error("Error updating internal domain:", error);
            setInternalDomainError(error?.error || "Failed to update domain");
            toast.error(error?.error || "Failed to update domain");
        }
    };

    const handleCancelInternalDomain = () => {
        setTempInternalDomain(
            workflowInternalDomain?.replace(".agentui.app", "") || ""
        );
        setEditingInternalDomain(false);
        setInternalDomainError(null);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success(t("common.copied") || "Copied to clipboard");
    };

    const copyDomainUrl = (domain) => {
        copyToClipboard(`https://${domain}`);
    };

    // Get server IP for DNS instructions (this would come from environment or API)
    const serverIP = Bluelagoon?.settings?.server?.ip || "3.128.149.149";
    const serverHostname =
        Bluelagoon?.settings?.server?.hostname || "proxy-api.agentui.app";

    // Extract subdomain from custom domain for DNS instructions
    // e.g., "app.koloto.ai" -> "app", "koloto.ai" -> "@"
    const getDnsHost = (domain) => {
        if (!domain) return "@";
        const parts = domain.split(".");
        // If 2 parts (e.g., "koloto.ai"), it's root domain -> @
        // If 3+ parts (e.g., "app.koloto.ai"), extract subdomain
        if (parts.length <= 2) return "@";
        return parts.slice(0, -2).join(".");
    };

    const dnsHost = getDnsHost(workflowCustomDomain);

    return (
        <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Globe className="w-6 h-6 text-blue-600" />
                        {t("appPreview.domains") || "App Domains"}
                    </h1>
                    <p className="text-sm text-gray-600 mt-2">
                        {t("appPreview.domainsDescription") ||
                            "Manage your app's domain configuration"}
                    </p>
                </div>

                {/* Internal Domain Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        {t("appPreview.internalDomain") || "AgentUI Domain"}
                    </h2>

                    {editingInternalDomain ? (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={tempInternalDomain}
                                    onChange={(e) =>
                                        setTempInternalDomain(e.target.value)
                                    }
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="my-app"
                                />
                                <span className="text-gray-500 font-medium">
                                    .agentui.app
                                </span>
                            </div>
                            {internalDomainError && (
                                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-600">
                                        {internalDomainError}
                                    </p>
                                </div>
                            )}
                            <p className="text-xs text-gray-500">
                                {t("appPreview.internalDomainHelp") ||
                                    "Use lowercase letters, numbers, and hyphens only"}
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSaveInternalDomain}
                                    disabled={
                                        isUpdatingWorkflow ||
                                        !tempInternalDomain.trim()
                                    }
                                    className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Check className="w-4 h-4" />
                                    {t("common.save") || "Save"}
                                </button>
                                <button
                                    onClick={handleCancelInternalDomain}
                                    disabled={isUpdatingWorkflow}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <X className="w-4 h-4" />
                                    {t("common.cancel") || "Cancel"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div
                                onClick={() => setEditingInternalDomain(true)}
                                className="px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                            >
                                {workflowInternalDomain ? (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Globe className="w-5 h-5 text-blue-600" />
                                            <span className="text-sm font-medium text-gray-900">
                                                {workflowInternalDomain}
                                            </span>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyDomainUrl(
                                                    workflowInternalDomain
                                                );
                                            }}
                                            className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors flex items-center gap-1.5"
                                        >
                                            <Copy className="w-4 h-4" />
                                            Copy URL
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-gray-400 text-sm flex items-center gap-2">
                                        <Globe className="w-4 h-4" />
                                        Click to add internal domain
                                    </span>
                                )}
                            </div>
                            {workflowInternalDomain && (
                                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-900">
                                            {workflowInternalDomain}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Your app is accessible at this URL
                                        </p>
                                    </div>
                                    <button
                                        onClick={() =>
                                            window.open(
                                                `https://${workflowInternalDomain}`,
                                                "_blank"
                                            )
                                        }
                                        className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors flex items-center gap-1.5"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        Open
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Custom Domain Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        {t("appPreview.customDomain") ||
                            "Custom Domain (Optional)"}
                    </h2>

                    {/* Plan Restriction Alert */}
                    {!isCustomDomainFeatureEnabled && (
                        <PlanRestrictionAlert
                            titleKey="appPreview.upgradeRequired"
                            titleFallback="Upgrade Required"
                            descriptionKey="appPreview.customDomainUpgradeMessage"
                            descriptionFallback="Custom domains are available on Creator plan and above. Upgrade your plan to use your own domain."
                            variant="inline"
                        />
                    )}

                    {editingCustomDomain ? (
                        <div className="space-y-4">
                            <input
                                type="text"
                                value={tempCustomDomain}
                                onChange={(e) =>
                                    setTempCustomDomain(e.target.value)
                                }
                                disabled={!isCustomDomainFeatureEnabled}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder="app.example.com"
                            />
                            {customDomainError && (
                                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-600">
                                        {customDomainError}
                                    </p>
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSaveCustomDomain}
                                    disabled={
                                        isUpdatingWorkflow ||
                                        !isCustomDomainFeatureEnabled
                                    }
                                    className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Check className="w-4 h-4" />
                                    {t("common.save") || "Save"}
                                </button>
                                <button
                                    onClick={handleCancelCustomDomain}
                                    disabled={isUpdatingWorkflow}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <X className="w-4 h-4" />
                                    {t("common.cancel") || "Cancel"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div
                                onClick={() => {
                                    if (isCustomDomainFeatureEnabled) {
                                        setEditingCustomDomain(true);
                                    }
                                }}
                                className={`px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg transition-colors ${
                                    isCustomDomainFeatureEnabled
                                        ? "cursor-pointer hover:border-blue-500 hover:bg-blue-50"
                                        : "cursor-not-allowed opacity-50"
                                }`}
                            >
                                {workflowCustomDomain ? (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900">
                                            {workflowCustomDomain}
                                        </span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyDomainUrl(
                                                    workflowCustomDomain
                                                );
                                            }}
                                            className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors flex items-center gap-1.5"
                                        >
                                            <Copy className="w-4 h-4" />
                                            Copy URL
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-gray-400 text-sm">
                                        Click to add custom domain
                                    </span>
                                )}
                            </div>

                            {/* DNS Configuration Instructions */}
                            {workflowCustomDomain && (
                                <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <h3 className="text-sm font-semibold text-blue-900 mb-2">
                                                {t(
                                                    "appPreview.dnsConfiguration"
                                                ) ||
                                                    "DNS Configuration Required"}
                                            </h3>
                                            <p className="text-xs text-blue-700 mb-3">
                                                {t(
                                                    "appPreview.dnsInstructions"
                                                ) ||
                                                    "Add the following DNS records to your domain to make it work:"}
                                            </p>

                                            <div className="space-y-3">
                                                <div className="bg-white p-3 rounded border border-blue-200">
                                                    <p className="text-xs font-semibold text-gray-700 mb-2">
                                                        Option 1: A Record
                                                    </p>
                                                    <div className="space-y-1 text-xs font-mono">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-600">
                                                                Type:
                                                            </span>
                                                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                                                                A
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-600">
                                                                Host:
                                                            </span>
                                                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                                                                {dnsHost}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-600">
                                                                Value:
                                                            </span>
                                                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                                                                {serverIP}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="bg-white p-3 rounded border border-blue-200">
                                                    <p className="text-xs font-semibold text-gray-700 mb-2">
                                                        Option 2: CNAME Record
                                                    </p>
                                                    <div className="space-y-1 text-xs font-mono">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-600">
                                                                Type:
                                                            </span>
                                                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                                                                CNAME
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-600">
                                                                Host:
                                                            </span>
                                                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                                                                {dnsHost}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-600">
                                                                Value:
                                                            </span>
                                                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                                                                {serverHostname}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <p className="text-xs text-blue-700 mt-3">
                                                {t("appPreview.dnsNote") ||
                                                    "Note: DNS changes can take up to 48 hours to propagate globally."}
                                            </p>
                                            <a
                                                href="https://help.agentui.ai/doc/add-domain-to-app"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                {t("appPreview.learnMoreDns") ||
                                                    "Learn more about adding a custom domain"}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Help Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        {t("appPreview.help") || "Help"}
                    </h2>
                    <div className="space-y-2 text-sm text-gray-600">
                        <p>
                            •{" "}
                            {t("appPreview.internalDomainHelpFull") ||
                                "Your AgentUI domain is automatically generated and always available"}
                        </p>
                        <p>
                            •{" "}
                            {t("appPreview.customDomainHelpFull") ||
                                "Custom domains allow you to use your own domain name for your app"}
                        </p>
                        <p>
                            •{" "}
                            {t("appPreview.dnsHelp") ||
                                "Contact your DNS provider to add the required DNS records"}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DomainsPanel;
